/**
 * useTableSort - Hook for managing table sorting
 */

import { useState, useCallback } from 'react';

/**
 * Sorting direction enum
 */
export const SORT_DIRECTION = {
  ASC: 'asc',
  DESC: 'desc',
  NONE: null,
};

/**
 * Hook to manage sorting state for table columns
 * @param {Array} data - Array of data to sort
 * @param {string} initialSortKey - Initial column to sort by
 * @param {string} initialDirection - Initial sort direction (asc/desc)
 * @returns {Object} - { sortedData, sortKey, direction, handleSort }
 */
export const useTableSort = (data = [], initialSortKey = null, initialDirection = SORT_DIRECTION.DESC) => {
  const [sortKey, setSortKey] = useState(initialSortKey);
  const [direction, setDirection] = useState(initialDirection);

  const sortedData = useCallback(() => {
    if (!sortKey || !data || data.length === 0) {
      return data;
    }

    const dataCopy = [...data];
    
    dataCopy.sort((a, b) => {
      const aValue = a[sortKey];
      const bValue = b[sortKey];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Handle numeric values
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return direction === SORT_DIRECTION.ASC ? aValue - bValue : bValue - aValue;
      }

      // Handle string values
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue, undefined, { numeric: true });
        return direction === SORT_DIRECTION.ASC ? comparison : -comparison;
      }

      // Fallback comparison
      if (aValue < bValue) return direction === SORT_DIRECTION.ASC ? -1 : 1;
      if (aValue > bValue) return direction === SORT_DIRECTION.ASC ? 1 : -1;
      return 0;
    });

    return dataCopy;
  }, [data, sortKey, direction]);

  const handleSort = useCallback((key) => {
    if (sortKey === key) {
      // Toggle direction if clicking same column
      if (direction === SORT_DIRECTION.ASC) {
        setDirection(SORT_DIRECTION.DESC);
      } else {
        setDirection(SORT_DIRECTION.ASC);
      }
    } else {
      // Set new sort key with descending order
      setSortKey(key);
      setDirection(SORT_DIRECTION.DESC);
    }
  }, [sortKey, direction]);

  return {
    sortedData: sortedData(),
    sortKey,
    direction,
    handleSort,
  };
};

export default useTableSort;
